import {
  type DocumentData,
  type Firestore,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import {
  LoanSchema,
  LoanCreateInputSchema,
  LoanUpdateInputSchema,
  type Loan,
  type LoanCreateInput,
  type LoanUpdateInput,
} from '@taki/data-model';
import { CURRENT_SCHEMA_VERSION, buildUpdateData, createConverter } from './standard-fields.js';
import { type CountOptions, type ListOptions, runCount, runList } from './list-query.js';
import { createArchiveOps } from './archive-ops.js';

/**
 * Repository de la famille `loan` (prêt d'un bien, org-wide en v0).
 *
 * Réplique le PATRON `asset` : l'entrée est VALIDÉE par Zod
 * (`LoanCreateInputSchema`) AVANT toute écriture Firestore. Les champs standard
 * sont remplis par le SDK, les requêtes `list` sont cadrées par orgId.
 */

export type { LoanCreateInput };

/** Nom de la collection Firestore de la famille loan. */
export const LOANS_COLLECTION = 'loans';

/**
 * Repository typé pour la famille `loan`.
 * Toute lecture est validée par `LoanSchema`, toute écriture par
 * `LoanCreateInputSchema` (cage IA : aucun accès Firestore direct hors SDK).
 */
export interface LoanRepository {
  create(input: LoanCreateInput): Promise<Loan>;
  update(id: string, patch: LoanUpdateInput): Promise<Loan>;
  list(options?: ListOptions): Promise<Loan[]>;
  count(options?: CountOptions): Promise<number>;
  get(id: string): Promise<Loan | null>;
  /** Archive (`archivedAt`). Semantique commune : cf. archive-ops.ts. */
  archive(id: string): Promise<Loan>;
  /** Desarchive (`archivedAt` = null, JAMAIS deleteField). Cf. archive-ops.ts. */
  unarchive(id: string): Promise<Loan>;
  /** Supprime (`deletedAt` + `archivedAt`), invisible cote client. Cf. archive-ops.ts. */
  remove(id: string): Promise<Loan>;
}

const loanConverter = createConverter<Loan>((raw: DocumentData) => LoanSchema.parse(raw));

/**
 * @param getActorId - retourne l'uid de l'utilisateur authentifié courant
 *   (injecté par `createTakiSdk`). Les Security Rules exigent
 *   createdBy/updatedBy == request.auth.uid.
 */
export function createLoanRepository(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
): LoanRepository {
  const col = collection(db, LOANS_COLLECTION).withConverter(loanConverter);
  // Archiver, desarchiver, supprimer : la semantique des trois etats vit dans
  // `createArchiveOps` (archive-ops.ts), ecrite une seule fois pour les 55
  // familles. Une famille n'a plus rien a decider ici.
  const archiveOps = createArchiveOps<Loan>(db, col, LOANS_COLLECTION, getActorId);

  return {
    async create(input: LoanCreateInput): Promise<Loan> {
      // Valider AVANT d'écrire : une entrée invalide échoue ici, rien n'atteint
      // Firestore. (Même doctrine que le patron asset.)
      const fields = LoanCreateInputSchema.parse(input);

      const actor = getActorId();
      const ref = doc(col);
      const now = new Date();

      // Champs métier optionnels : on ne les écrit que s'ils sont fournis
      // (évite d'ancrer des `undefined` / `null` parasites dans Firestore).
      const optional: DocumentData = {};
      if (fields.lentAt !== undefined) optional['lentAt'] = fields.lentAt;
      if (fields.dueBackAt !== undefined) optional['dueBackAt'] = fields.dueBackAt;
      if (fields.returnedAt !== undefined) optional['returnedAt'] = fields.returnedAt;
      if (fields.conditionNotes !== undefined) optional['conditionNotes'] = fields.conditionNotes;
      if (fields.billable !== undefined) optional['billable'] = fields.billable;
      if (fields.orderId !== undefined) optional['orderId'] = fields.orderId;
      if (fields.custodyId !== undefined) optional['custodyId'] = fields.custodyId;
      if (fields.conditionInspectionIds !== undefined) optional['conditionInspectionIds'] = fields.conditionInspectionIds;
      if (fields.incidentId !== undefined) optional['incidentId'] = fields.incidentId;
      if (fields.customFields !== undefined) optional['customFields'] = fields.customFields;

      const payload: DocumentData = {
        orgId: fields.orgId,
        type: fields.type,
        itemAssetId: fields.itemAssetId,
        borrowerContactId: fields.borrowerContactId,
        ...optional,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: actor,
        updatedBy: actor,
        archivedAt: null,
        deletedAt: null,
        schemaVersion: CURRENT_SCHEMA_VERSION,
      };

      await setDoc(ref, payload as unknown as Loan);

      // setDoc ne résout pas serverTimestamp() : on valide une projection
      // cohérente avec ce qui vient d'être écrit.
      return LoanSchema.parse({
        id: ref.id,
        ...payload,
        createdAt: now,
        updatedAt: now,
      });
    },

    async update(id: string, patch: LoanUpdateInput): Promise<Loan> {
      // Valider AVANT d'écrire : même doctrine que create.
      const fields = LoanUpdateInputSchema.parse(patch);
      const actor = getActorId();

      // Seules les clés réellement fournies sont écrites (patch partiel, merge).
      // Patch transforme pour updateDoc : customFields est eclate en chemins
      // pointes ('customFields.<cle>') pour fusionner cle par cle au lieu de
      // remplacer la map entiere (cf. buildUpdateData ; null = deleteField).
      const data = buildUpdateData(fields);
      data['updatedAt'] = serverTimestamp();
      data['updatedBy'] = actor;

      // Ref brute (sans converter) pour l'écriture partielle : une ref convertie
      // exigerait le document complet.
      await updateDoc(doc(db, LOANS_COLLECTION, id), data);

      // Relire via la ref convertie : le document réel est la source de vérité,
      // et le converter le valide avec LoanSchema.
      const snap = await getDoc(doc(col, id));
      if (!snap.exists()) {
        throw new Error(`loan ${id} introuvable après mise à jour.`);
      }
      return snap.data();
    },

    async list(options?: ListOptions): Promise<Loan[]> {
      // Cadrage orgId, exclusion des archives, sous-type, tri, taille de page
      // et curseur : toute la semantique vit dans `runList` (list-query.ts),
      // ecrite une seule fois pour les 55 familles.
      return runList(col, orgId, options);
    },

    async count(options?: CountOptions): Promise<number> {
      // Compter sans charger : meme cadrage que `list`, sans page ni tri
      // (cf. runCount, list-query.ts). Un compteur calcule sur la page
      // courante ment des que la liste est paginee.
      return runCount(col, orgId, options);
    },

    async get(id: string): Promise<Loan | null> {
      const ref = doc(col, id);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return snap.data();
    },

    // archive / unarchive / remove : cf. `archiveOps` plus haut.
    ...archiveOps,
  };
}
